/* ============================================================================
 * ЖУРНАЛ ИНЦИДЕНТОВ (§6.3, 2026-10-07)
 *
 * Постоянный журнал в PostGIS (incident_journal): события zone_guard,
 * trust-алерты и одобрения «через конфликт» переживают рестарт консоли.
 * Данные — GET /api/incidents (роль auditor; ЦУП через catch-all).
 * Ничего не выдумываем: недоступность БД показываем как деградацию,
 * пустой журнал — как «записей нет», а не нули.
 * ========================================================================== */

import { ScrollText, Info } from 'lucide-react'

import { EmptyHint, Metric, Panel } from '../ui/Panels'
import { StateBadge, StateDot } from '../ui/State'
import type { AsyncState, IncidentsSnapshot } from '../../types'

/** Русские подписи родов событий (неизвестный род — как есть) */
const KIND_LABEL: Record<string, string> = {
  zone_intrusion: 'Вторжение в запретную зону',
  declaration_zone_conflict: 'Одобрение через конфликт',
  trust_diverged: 'Отклонение от маршрута',
  trust_predict_conflict: 'Прогноз конфликта',
}

function kindLabel(kind: string): string {
  return KIND_LABEL[kind] ?? kind
}

function severityDot(sev: string): 'active' | 'warning' {
  const s = String(sev ?? '').toLowerCase()
  return s === 'critical' || s === 'high' ? 'warning' : 'active'
}

export function IncidentsJournal({
  incidents,
  expanded = false,
}: {
  incidents: AsyncState<IncidentsSnapshot>
  expanded?: boolean
}) {
  const snap = incidents.data
  const rows = snap?.incidents ?? []
  const shown = expanded ? rows : rows.slice(0, 8)
  const critical = rows.filter(
    (r) => String(r.severity ?? '').toLowerCase() === 'critical',
  ).length

  /* -------- состояние загрузки -------- */
  if (!snap) {
    return (
      <Panel title="Журнал инцидентов (БД)" icon={ScrollText}>
        <EmptyHint>
          <span className="flex flex-col items-center gap-1.5">
            <Info size={18} className="text-ink-muted" aria-hidden />
            {incidents.status === 'loading'
              ? 'Запрос /api/incidents…'
              : 'Журнал недоступен. Проверьте консоль ЦУП на :8105.'}
            {incidents.error && (
              <span className="max-w-[280px] text-ink-muted">{incidents.error}</span>
            )}
          </span>
        </EmptyHint>
      </Panel>
    )
  }

  return (
    <Panel
      title="Журнал инцидентов (БД)"
      icon={ScrollText}
      state={
        <StateBadge state={critical ? 'warning' : 'active'}>
          {snap.total_all ?? snap.total}
        </StateBadge>
      }
    >
      {/* --------- метрики --------- */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Metric label="Всего записей" value={String(snap.total_all ?? snap.total)} state="active" />
        <Metric
          label="Критических"
          value={String(critical)}
          state={critical ? 'warning' : 'active'}
        />
        <Metric label="Родов событий" value={String(Object.keys(snap.by_kind ?? {}).length)} state="active" />
        <Metric
          label="Источник"
          value={snap.ok ? 'PostGIS' : 'деградация'}
          state={snap.ok ? 'active' : 'warning'}
        />
      </div>

      {/* --------- деградация --------- */}
      {(!snap.ok || incidents.status === 'error' || incidents.status === 'degraded') && (
        <div className="flex items-center gap-2 px-2 py-2 text-[11px] text-ink-muted">
          <StateDot state="warning" />
          {snap.error ?? incidents.error ?? 'журнал недоступен'} — события в памяти
          (trust/zone_guard) продолжают приходить, история не пишется
        </div>
      )}

      {/* --------- список --------- */}
      {rows.length === 0 ? (
        <div className="flex items-center gap-2 px-2 py-3 text-[12px] text-ink-muted">
          <StateDot state="active" />
          Записей нет — инцидентов с момента старта журнала не зафиксировано
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          {shown.map((r) => (
            <div key={r.id} className="rounded border border-line px-2 py-1.5 text-[11px]">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 font-semibold">
                  <StateDot state={severityDot(r.severity)} />
                  {kindLabel(r.kind)}
                </span>
                <span className="shrink-0 text-ink-muted">{r.ts}</span>
              </div>
              <div className="text-ink-muted">
                {r.message || '—'}
                {r.drone ? ` · БВС ${r.drone}` : ''}
              </div>
            </div>
          ))}
          {!expanded && rows.length > shown.length && (
            <div className="px-2 py-1 text-[11px] text-ink-muted">
              показаны последние {shown.length} из {rows.length} — полная история в БД
            </div>
          )}
          {expanded && rows.length > 200 && (
            <div className="px-2 py-1 text-[11px] text-ink-muted">
              показаны последние 200 — всего в журнале {snap.total_all ?? snap.total}
            </div>
          )}
        </div>
      )}

      {/* --------- экспорт (п.7/P2c): GET /api/incident/export?fmt=… --------- */}
      <div className="mt-2 flex flex-wrap items-center gap-2 px-2 text-[11px]">
        <span className="text-ink-muted">Отчёт:</span>
        {(['csv', 'html', 'pdf', 'json'] as const).map((fmt) => (
          <a
            key={fmt}
            className="rounded border border-line px-2 py-0.5 uppercase tracking-wide text-hmi-link no-underline hover:bg-hmi-panel2"
            href={`/api/incident/export?fmt=${fmt}`}
            onClick={(e) => {
              // скачивание, а не переход: формируем blob из JSON/текста ответа
              e.preventDefault()
              fetch(`/api/incident/export?fmt=${fmt}`, { credentials: 'include' })
                .then((r) => (fmt === 'json' ? r.json() : r.text()))
                .then((data) => {
                  const text = typeof data === 'string' ? data : JSON.stringify(data, null, 2)
                  const ct = fmt === 'json' ? 'application/json' : fmt === 'pdf' ? 'application/pdf' : 'text/plain'
                  const url = URL.createObjectURL(new Blob([text], { type: ct }))
                  const a = Object.assign(document.createElement('a'), { href: url, download: `incidents.${fmt === 'json' ? 'json' : fmt}` })
                  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url)
                })
                .catch(() => alert('Экспорт отчёта недоступен'))
            }}
          >
            {fmt}
          </a>
        ))}
      </div>
    </Panel>
  )
}
